import { h, mount } from '../lib/dom.js';
import { apiGet } from '../lib/api.js';
import { renderShell, pageHead, empty, loading } from '../components/shell.js';
import { taskRow } from '../components/task.js';
import { fmtDate, fmtDateRange, fmtDuration, dayName, todayIso, statusLabel, statusIcon } from '../lib/format.js';
import { weekCard, weekGroup, viewSwitch, groupWeeks } from '../components/week.js';
import { renderWeekSpread } from '../components/weekSpread.js';
import { noteCard } from '../components/note.js';
import { renderWeekReport } from '../components/weekReport.js';
import { studentNoteCard, openStudentNoteModal, CATEGORY_KEYS, categoryLabel } from '../components/studentNote.js';
import { apiGet as _apiGet } from '../lib/api.js';

export async function renderStudentDashboard({ outlet }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());

  const data = await apiGet('/dashboard/student');
  container.replaceChildren();

  const today = data.today;
  container.appendChild(pageHead(
    dayName(today) + ', ' + fmtDate(today),
    'Bugün planlanan ve şimdi yapman gereken.'
  ));

  container.appendChild(renderWeeklySummary(data));

  const section = h('section', { class: 'stack', style: { '--stack-gap': 'var(--sp-3)' } });
  section.appendChild(h('div', { class: 'section-header' },
    h('h3', {}, 'Bugünkü görevler'),
    h('span', { class: 'count' }, data.today_tasks.length + ' görev'),
  ));
  if (data.today_tasks.length === 0) {
    section.appendChild(empty('Bugün için görev yok.', 'Koç bir sonraki günü planladığında burada görünür.'));
  } else {
    data.today_tasks.forEach(t => section.appendChild(taskRow(t, {
      canEdit: false, canComplete: true, onChange: () => renderStudentDashboard({ outlet }),
    })));
  }
  container.appendChild(section);

  if (data.last_coach_note) {
    container.appendChild(h('section', { style: { marginTop: 'var(--sp-6)' } },
      h('div', { class: 'section-header' }, h('h3', {}, 'Koçtan son not')),
      noteCard(data.last_coach_note),
    ));
  }
}

function renderWeeklySummary(data) {
  const week = data.week, sum = data.week_summary;
  if (!week) {
    return h('div', { class: 'card' },
      h('div', { class: 'stack-sm stack' },
        h('div', { class: 'eyebrow' }, 'Bu hafta'),
        h('h3', {}, 'Aktif hafta henüz oluşturulmadı.'),
        h('p', { class: 'muted' }, 'Koç bir hafta yayınladığında burada özet göreceksin.'),
      ),
    );
  }
  const progress = sum?.time_progress ?? 0;
  const bar = h('div', { class: 'progress' },
    h('div', {
      class: 'progress-bar' + (progress >= 100 ? ' ok' : (progress < 60 ? ' warn' : '')),
      style: { width: Math.min(100, progress) + '%' }
    })
  );
  const done = sum?.counts?.completed ?? 0;
  const total = sum?.counts?.total ?? 0;

  return h('div', { class: 'card' },
    h('div', { class: 'row-between row-wrap' },
      h('div', {},
        h('div', { class: 'eyebrow' }, 'Bu hafta'),
        h('h2', {}, week.title || fmtDateRange(week.start_date, week.end_date)),
        week.title ? h('p', { class: 'muted' }, fmtDateRange(week.start_date, week.end_date)) : null,
      ),
      h('a', { href: '#/student/week', class: 'btn btn-secondary btn-sm' }, 'Haftayı aç →'),
    ),
    week.weekly_goal_text ? h('p', { style: { marginTop: 'var(--sp-3)', color: 'var(--color-text)', fontStyle: 'italic' } }, '“' + week.weekly_goal_text + '”') : null,
    h('div', { class: 'grid grid-3', style: { marginTop: 'var(--sp-4)' } },
      h('div', { class: 'stat' },
        h('span', { class: 'stat-label' }, 'Görevler'),
        h('span', { class: 'stat-value' }, done + ' / ' + total),
        h('span', { class: 'stat-sub' }, (sum?.completion_rate ?? 0) + '% tamamlama'),
      ),
      h('div', { class: 'stat' },
        h('span', { class: 'stat-label' }, 'Süre'),
        h('span', { class: 'stat-value' }, fmtDuration(sum?.actual_minutes ?? 0)),
        h('span', { class: 'stat-sub' }, 'Hedef: ' + fmtDuration(sum?.target_minutes ?? 0)),
      ),
      h('div', { class: 'stat' },
        h('span', { class: 'stat-label' }, 'Sorular'),
        h('span', { class: 'stat-value' }, (sum?.question_actual ?? 0) + ''),
        h('span', { class: 'stat-sub' }, sum?.question_target ? 'Hedef: ' + sum.question_target : '—'),
      ),
    ),
    h('div', { style: { marginTop: 'var(--sp-4)' } }, bar),
  );
}

// -- Student week view: current week or specific week by :id --
export async function renderStudentWeek({ outlet, params }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());

  let week;
  if (params?.id) {
    const res = await apiGet('/weeks/' + params.id);
    week = res.week;
  } else {
    const cur = await apiGet('/weeks/current');
    week = cur.week;
  }
  container.replaceChildren();

  if (!week) {
    container.appendChild(pageHead(
      'Bu Hafta',
      'Bugünü kapsayan aktif bir hafta yok.',
      h('a', { class: 'btn btn-secondary', href: '#/student/weeks' }, 'Tüm haftalar →'),
    ));
    container.appendChild(empty(
      'Aktif hafta yok',
      'Koç bu haftayı henüz oluşturmadı. Haftalar sekmesinden geçmiş ya da yaklaşan haftalara bakabilirsin.',
    ));
    return;
  }

  const { tasks } = await apiGet('/weeks/' + week.id + '/tasks');
  container.appendChild(pageHead(
    week.title || fmtDateRange(week.start_date, week.end_date),
    fmtDateRange(week.start_date, week.end_date) + (week.weekly_goal_text ? ' · Hedef: ' + week.weekly_goal_text : ''),
    h('a', { class: 'btn btn-ghost btn-sm', href: '#/student/weeks' }, '← Tüm haftalar'),
    h('a', { class: 'btn btn-secondary btn-sm', href: '#/student/weeks/' + week.id + '/report' }, 'Rapor →'),
  ));

  container.appendChild(renderWeekSpread(week, tasks, {
    canEdit: false,
    onChange: () => renderStudentWeek({ outlet, params }),
  }));
}

// -- Student: list of all weeks --
export async function renderStudentWeeks({ outlet, query }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());
  const view = query?.view || 'active'; // 'active' | 'completed'
  const { weeks } = await apiGet('/weeks?limit=50');
  container.replaceChildren();

  const refresh = () => renderStudentWeeks({ outlet, query });
  const tabs = viewSwitch('/student/weeks', view, [
    ['active', 'Aktif'],
    ['completed', 'Tamamlanan'],
  ]);

  container.appendChild(pageHead(
    view === 'completed' ? 'Tamamlanan Haftalar' : 'Haftalar',
    view === 'completed'
      ? 'Tüm görevlerini bitirdiğin haftalar. En son bitene, üstte.'
      : 'Bu hafta, yaklaşanlar ve geçmiş haftalar. Bir haftaya tıklayınca planı ve ilerlemesini görürsün.',
    tabs,
  ));

  if (!weeks || weeks.length === 0) {
    container.appendChild(empty(
      'Henüz hafta yok',
      'Koç bir hafta oluşturduğunda burada göreceksin.',
    ));
    return;
  }

  const g = groupWeeks(weeks, todayIso());
  const build = (w, kind) => weekCard(w, {
    kind, refresh, canManage: false, hrefBase: '/student/weeks',
  });

  const wrap = h('div', { class: 'stack stack-lg' });
  if (view === 'completed') {
    const s = weekGroup('Tamamlanan', g.completed, 'completed', build);
    if (s) wrap.appendChild(s);
    else {
      container.appendChild(empty('Henüz tamamlanan hafta yok',
        'Bir haftadaki tüm görevleri tamamlayınca burada göreceksin.'));
      return;
    }
  } else {
    [
      weekGroup('Bu hafta', g.current, 'current', build),
      weekGroup('Yaklaşan', g.upcoming, 'upcoming', build),
      weekGroup('Geçmiş', g.past, 'past', build),
    ].forEach(s => s && wrap.appendChild(s));
    if (g.current.length + g.upcoming.length + g.past.length === 0) {
      container.appendChild(empty('Aktif hafta yok',
        'Tamamlanan haftalarını üstteki "Tamamlanan" sekmesinden görebilirsin.'));
      return;
    }
  }
  container.appendChild(wrap);
}

export async function renderStudentNotes({ outlet }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());
  const { notes } = await apiGet('/coach-notes');
  container.replaceChildren();
  container.appendChild(pageHead('Koç Notları', 'Koçun senin için bıraktığı görünür notlar.'));
  if (notes.length === 0) {
    container.appendChild(empty('Henüz not yok', 'Koç bir not eklediğinde burada görünür.'));
    return;
  }
  const list = h('div', { class: 'stack' });
  notes.forEach(n => list.appendChild(noteCard(n)));
  container.appendChild(list);
}

// -- Haftalık rapor — ortak weekReport bileşenini kullanır --
export async function renderStudentWeekReport({ outlet, params }) {
  return renderWeekReport({
    outlet,
    weekId: params.id,
    backHref: '/student/weeks/' + params.id,
    audience: 'student',
  });
}

// -- Öğrenci notları — kendi CRUD'u --
export async function renderStudentMyNotes({ outlet, query }) {
  const container = h('div');
  mount(outlet, renderShell(container));
  container.appendChild(loading());
  const category = query?.category || 'all';
  const url = '/student-notes' + (category !== 'all' ? '?category=' + category : '');
  const { notes } = await apiGet(url);
  container.replaceChildren();
  const refresh = () => renderStudentMyNotes({ outlet, query });

  container.appendChild(pageHead(
    'Notlarım',
    'Aklından geçenler, ders çıkarımların, tekrar edilecek her şey. Koç da bu notları görebilir.',
    h('button', { class: 'btn btn-primary',
      onclick: () => openStudentNoteModal(null, refresh),
    }, '+ Yeni not'),
  ));

  // Kategori filtresi
  const catTabs = h('div', { class: 'category-tabs' });
  const catLink = (k, label) => h('a', {
    href: k === 'all' ? '#/student/my-notes' : '#/student/my-notes?category=' + k,
    class: 'btn btn-sm ' + (category === k ? 'btn-primary' : 'btn-ghost'),
  }, label);
  catTabs.appendChild(catLink('all', 'Tümü'));
  CATEGORY_KEYS.forEach(k => catTabs.appendChild(catLink(k, categoryLabel(k))));
  container.appendChild(catTabs);

  if (!notes || notes.length === 0) {
    container.appendChild(empty(
      category === 'all' ? 'Henüz not yok' : categoryLabel(category) + ' kategorisinde not yok',
      'İlk notunu ekleyerek başla. Küçük bir gözlem bile geleceğine ışık tutar.',
    ));
    return;
  }
  const list = h('div', { class: 'stack' });
  notes.forEach(n => list.appendChild(studentNoteCard(n, {
    canManage: true,
    onEdit: (note) => openStudentNoteModal(note, refresh),
    refresh,
  })));
  container.appendChild(list);
}
